/**
 * AdminInvitations — team-onboarding block for the Admin page.
 *
 * Extracted from `pages/Admin.jsx` (previously 838 lines, complexity flagged
 * by code review) so the invitation list, single-invite dialog, and bulk-CSV
 * dialog live together in one focused unit.
 *
 * Props
 * -----
 *   roles: string[]           — role names loaded from `/admin/roles`
 *                                (passed in so we don't duplicate the fetch)
 *
 * Responsibilities
 * ----------------
 *   - Fetches its own `/admin/invites` list.
 *   - Owns state for the "Invite user" and "Bulk invite (CSV)" dialogs.
 *   - Handles resend, revoke, and copy-link actions.
 *
 * All existing `data-testid`s from the previous Admin.jsx are preserved so
 * the testing agent's browser flows keep working without changes.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, Copy, Mail, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";


// CSV → array of {email, name, roles[]}. Header row is optional. Roles are
// separated by ';' or '|' inside the CSV cell so we don't collide with commas.
const parseBulkCsv = (text) => {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const start = /email/i.test(lines[0]) && !lines[0].includes("@") ? 1 : 0;
  const out = [];
  for (const line of lines.slice(start)) {
    const cells = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const [email, name, rolesCell] = cells;
    if (!email || !email.includes("@")) continue;
    const roles = (rolesCell || "").split(/[;|]/).map((r) => r.trim()).filter(Boolean);
    out.push({ email, name: name || undefined, roles });
  }
  return out;
};


export default function AdminInvitations({ roles = [] }) {
  const [invites, setInvites] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", name: "", roles: [], expires_in_hours: 72,
  });
  const [freshInvite, setFreshInvite] = useState(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkExpiry, setBulkExpiry] = useState(72);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const loadInvites = async () => {
    try {
      const { data } = await api.get("/admin/invites");
      setInvites(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invitations");
    }
  };
  useEffect(() => { loadInvites(); }, []);

  const toggleInviteRole = (r) => setInviteForm((f) => ({
    ...f,
    roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
  }));

  const submitInvite = async () => {
    if (!inviteForm.email) return toast.error("Email is required");
    try {
      const { data } = await api.post("/admin/invites", inviteForm);
      setFreshInvite(data);
      setInviteDialogOpen(false);
      setInviteForm({ email: "", name: "", roles: [], expires_in_hours: 72 });
      if (data.email_sent) toast.success(`Invite created — email sent to ${data.email}`);
      loadInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invite");
    }
  };

  const resendInviteEmail = async (id, email) => {
    try {
      const { data } = await api.post(`/admin/invites/${id}/resend-email`);
      if (data.email_sent) toast.success(`Email re-sent to ${email}`);
      else toast.info(data.note || "Email integration not configured — use the copy-link button instead", { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend email");
    }
  };

  const revokeInvite = async (id, email) => {
    if (!window.confirm(`Revoke the invitation for ${email}? They won't be able to use the link any more.`)) return;
    await api.delete(`/admin/invites/${id}`);
    toast.success("Invitation revoked");
    loadInvites();
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/accept-invite/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied — share via email or chat");
  };

  const bulkPreviewRows = parseBulkCsv(bulkCsv);
  const bulkInvalidRoles = Array.from(new Set(
    bulkPreviewRows.flatMap((r) => r.roles.filter((x) => !roles.includes(x)))
  ));

  const submitBulk = async () => {
    if (bulkPreviewRows.length === 0) return toast.error("No valid rows detected — check your CSV");
    if (bulkPreviewRows.length > 200) return toast.error("Maximum 200 invitations per batch");
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/admin/invites/bulk", {
        rows: bulkPreviewRows, expires_in_hours: bulkExpiry,
      });
      setBulkResult(data);
      toast.success(`Bulk invite: ${data.created} created, ${data.skipped} skipped, ${data.emails_sent} emailed`);
      loadInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk invite failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <>
      {/* Invitations list + action buttons */}
      <div className="mt-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="vq-overline mb-2">Onboarding</div>
            <h2 className="font-display font-bold text-2xl text-slate-900">Invite team members</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Send someone a signed invite link with roles already pre-assigned. When they open the link they choose a password and land on the dashboard ready to work.
            </p>
          </div>
          <Button onClick={() => setInviteDialogOpen(true)} data-testid="admin-invite-new-btn"
                  className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Send className="w-4 h-4 mr-2" /> Invite user
          </Button>
          <Button onClick={() => { setBulkResult(null); setBulkDialogOpen(true); }}
                  data-testid="admin-invite-bulk-btn" variant="outline"
                  className="h-11 border-slate-300 font-semibold">
            <Upload className="w-4 h-4 mr-2" /> Bulk invite (CSV)
          </Button>
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Invitee</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Pre-assigned roles</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Invited by</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.invite_id} className={`border-b border-slate-100 ${inv.status !== "pending" ? "opacity-60" : ""}`} data-testid={`admin-invite-row-${inv.invite_id}`}>
                  <td className="px-6 py-4">
                    <div className="font-display font-bold text-sm text-slate-900">{inv.name || inv.email}</div>
                    <div className="text-[10px] font-mono text-slate-500">{inv.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(inv.roles || []).length === 0
                        ? <span className="text-[10px] font-mono text-slate-500">no role</span>
                        : (inv.roles || []).map((r) => (
                          <span key={r} className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded text-slate-700 bg-slate-50">{r}</span>
                        ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs font-mono text-slate-600">{inv.invited_by_email}</td>
                  <td className="px-6 py-4 text-xs">
                    {inv.status === "pending" && <span className="px-1.5 py-0.5 rounded font-mono text-[10px] bg-blue-50 text-blue-700 border border-blue-200">PENDING</span>}
                    {inv.status === "accepted" && <span className="px-1.5 py-0.5 rounded font-mono text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">ACCEPTED</span>}
                    {inv.status === "expired" && <span className="px-1.5 py-0.5 rounded font-mono text-[10px] bg-slate-100 text-slate-500 border border-slate-200">EXPIRED</span>}
                    <div className="mt-1 text-[10px] font-mono text-slate-500">
                      {inv.status === "accepted" && inv.accepted_at ? `on ${new Date(inv.accepted_at).toLocaleDateString()}` : `expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {inv.status === "pending" && (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline"
                                onClick={() => copyInviteLink(inv.token)}
                                disabled={!inv.token}
                                data-testid={`admin-invite-copy-${inv.invite_id}`}
                                className="border-slate-300 font-semibold"
                                title="Copy invite link to clipboard">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => resendInviteEmail(inv.invite_id, inv.email)}
                                data-testid={`admin-invite-resend-${inv.invite_id}`}
                                className="border-slate-300 font-semibold"
                                title="Re-send invitation email">
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => revokeInvite(inv.invite_id, inv.email)}
                                data-testid={`admin-invite-revoke-${inv.invite_id}`}
                                title="Revoke this invitation">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {invites.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">No invitations yet. Click &ldquo;Invite user&rdquo; to onboard someone.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create invitation dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Invite a team member</DialogTitle>
            <DialogDescription>
              They&apos;ll get a one-time link. Opening it lets them set a password and log in with the roles you pick below already assigned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Email *</Label>
              <Input data-testid="admin-invite-email" type="email" value={inviteForm.email}
                     onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                     className="mt-1.5" placeholder="colleague@company.com" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Name (optional)</Label>
              <Input data-testid="admin-invite-name" value={inviteForm.name}
                     onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                     className="mt-1.5" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Pre-assign roles</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1 border border-slate-200 rounded p-2 max-h-52 overflow-y-auto">
                {roles.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm p-1">
                    <Checkbox data-testid={`admin-invite-role-${r.replace(/\s/g, "-")}`}
                              checked={inviteForm.roles.includes(r)}
                              onCheckedChange={() => toggleInviteRole(r)} />
                    {r}
                  </label>
                ))}
              </div>
              <p className="text-[10px] font-mono text-slate-500 mt-1">Leave empty to invite a &ldquo;view-only&rdquo; user; you can always edit roles later.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Invite expires in (hours)</Label>
              <Input type="number" min={1} max={720} value={inviteForm.expires_in_hours}
                     data-testid="admin-invite-hours"
                     onChange={(e) => setInviteForm({ ...inviteForm, expires_in_hours: parseInt(e.target.value) || 72 })}
                     className="mt-1.5" />
              <p className="text-[10px] font-mono text-slate-500 mt-1">Max 720 hours (30 days). After that the link becomes invalid.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitInvite} data-testid="admin-invite-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">
              <Send className="w-4 h-4 mr-2" /> Create invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fresh invite — show link once with copy CTA */}
      <Dialog open={!!freshInvite} onOpenChange={() => setFreshInvite(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Invite ready — share this link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This is a one-time link for <span className="font-mono">{freshInvite?.email}</span>. They&apos;ll set their own password when they open it.
              Pre-assigned roles: <span className="font-mono">{(freshInvite?.roles || []).join(", ") || "none"}</span>.
            </p>
            <div className="p-3 bg-slate-900 text-white rounded font-mono text-xs break-all" data-testid="admin-invite-fresh-url">
              {window.location.origin}/accept-invite/{freshInvite?.token}
            </div>
            <Button onClick={() => copyInviteLink(freshInvite.token)} className="w-full bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Copy className="w-4 h-4 mr-2" /> Copy invite link
            </Button>
            <div className="text-[10px] font-mono text-slate-500 text-center">
              Expires {new Date(freshInvite?.expires_at || Date.now()).toLocaleString()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk invite (CSV) dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) { setBulkCsv(""); setBulkResult(null); } }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk invite from CSV</DialogTitle>
            <DialogDescription>
              Paste one row per invitee. Format: <span className="font-mono">email, name, roles</span> &mdash; separate roles inside a cell with <span className="font-mono">;</span>. Header row optional. Max 200 rows.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Available roles</Label>
                <div className="mt-1.5 text-xs font-mono text-slate-600">{roles.join(", ")}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold">CSV rows</Label>
                <Textarea
                  data-testid="admin-bulk-csv"
                  value={bulkCsv}
                  onChange={(e) => setBulkCsv(e.target.value)}
                  className="mt-1.5 font-mono text-xs h-40"
                  placeholder={`email,name,roles\njane@company.com,Jane Doe,QA Lead;RA\nmike@company.com,Mike Smith,Manufacturing`}
                />
                <p className="text-[10px] font-mono text-slate-500 mt-1">
                  Detected: <strong>{bulkPreviewRows.length}</strong> valid row(s)
                  {bulkInvalidRoles.length > 0 && (
                    <span className="text-rose-600"> · unknown roles: {bulkInvalidRoles.join(", ")}</span>
                  )}
                </p>
              </div>
              {bulkPreviewRows.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden max-h-56 overflow-y-auto" data-testid="admin-bulk-preview">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Email</th>
                        <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Name</th>
                        <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreviewRows.slice(0, 20).map((r) => (
                        <tr key={r.email} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono">{r.email}</td>
                          <td className="px-3 py-2">{r.name || "—"}</td>
                          <td className="px-3 py-2">
                            {r.roles.length === 0
                              ? <span className="text-slate-500 font-mono">no role</span>
                              : r.roles.map((rr) => (
                                <span key={rr} className={`inline-block mr-1 px-1.5 py-0.5 text-[10px] font-mono border rounded ${roles.includes(rr) ? "border-slate-200 text-slate-700 bg-slate-50" : "border-rose-200 text-rose-700 bg-rose-50"}`}>{rr}</span>
                              ))}
                          </td>
                        </tr>
                      ))}
                      {bulkPreviewRows.length > 20 && (
                        <tr><td colSpan={3} className="px-3 py-2 text-center font-mono text-slate-500">…and {bulkPreviewRows.length - 20} more row(s)</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold">All invites expire in (hours)</Label>
                <Input type="number" min={1} max={720} value={bulkExpiry}
                       data-testid="admin-bulk-hours"
                       onChange={(e) => setBulkExpiry(parseInt(e.target.value) || 72)}
                       className="mt-1.5" />
              </div>
            </div>
          )}

          {bulkResult && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-2 py-1 rounded font-mono text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="admin-bulk-created-count">
                  Created: {bulkResult.created}
                </span>
                <span className="px-2 py-1 rounded font-mono text-xs bg-slate-50 text-slate-700 border border-slate-200">
                  Skipped: {bulkResult.skipped}
                </span>
                <span className="px-2 py-1 rounded font-mono text-xs bg-blue-50 text-blue-700 border border-blue-200">
                  Emails sent: {bulkResult.emails_sent}
                </span>
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Email</th>
                      <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-left px-3 py-2 font-mono uppercase tracking-wider text-slate-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResult.results.map((r) => (
                      <tr key={r.email} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono">{r.email}</td>
                        <td className="px-3 py-2">
                          {r.status === "created" && <span className="text-emerald-700 font-semibold">created</span>}
                          {r.status === "skipped" && <span className="text-slate-500">skipped</span>}
                          {r.status === "error" && <span className="text-rose-600 font-semibold">error</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {r.reason || (r.status === "created"
                            ? (r.email_sent ? "invite created + email sent" : "invite created (email not configured)")
                            : "")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                <Button onClick={submitBulk} disabled={bulkSubmitting || bulkPreviewRows.length === 0 || bulkInvalidRoles.length > 0}
                        data-testid="admin-bulk-submit"
                        className="bg-slate-900 hover:bg-[#0055FF] text-white">
                  <Send className="w-4 h-4 mr-2" />
                  {bulkSubmitting ? "Sending…" : `Send ${bulkPreviewRows.length} invitation${bulkPreviewRows.length === 1 ? "" : "s"}`}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setBulkResult(null); setBulkCsv(""); }}>
                  Do another batch
                </Button>
                <Button onClick={() => setBulkDialogOpen(false)}
                        className="bg-slate-900 hover:bg-[#0055FF] text-white">
                  Done
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
